import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  CameraIcon,
  MapPinIcon,
  UserGroupIcon,
  DocumentIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CalculatorIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  BriefcaseIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline';
import { useLoginMutation } from '../services/authApi';
import { authApi } from '../services/authApi';
import { selectAuthError, selectAuthLoading, clearError } from '../store/slices/authSlice';

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [login] = useLoginMutation();
  const authError = useSelector(selectAuthError);
  const authLoading = useSelector(selectAuthLoading);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('auth:invalidEmail'))
      .required(t('auth:emailRequired')),
    password: Yup.string()
      .min(6, t('auth:passwordMinLength'))
      .required(t('auth:passwordRequired')),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(clearError());
        const result = await login({
          email: values.email,
          password: values.password,
        }).unwrap();

        // Fetch complete user profile with employee data
        try {
          const profileResult = await dispatch(
            authApi.endpoints.getProfile.initiate()
          ).unwrap();
          console.log('Profile fetched successfully:', profileResult);
        } catch (profileError) {
          console.warn('Failed to fetch profile:', profileError);
        }

        // Success - navigate based on user role
        const userRole = result?.user?.role || result?.role;
        if (userRole === 'employee') {
          navigate('/dashboard');  // ← Direct to dashboard (which now shows employee portal content)
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        // Error is handled by RTK Query and stored in auth state
        console.error('Login failed:', error);
      }
    },
  });

  // Clear error when component unmounts or when user starts typing
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Check for success message from location state (e.g., from onboarding)
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the state to prevent showing the message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    // Add custom animations and 3D effects
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }

      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out forwards;
        opacity: 0;
      }

      .animate-shake {
        animation: shake 0.5s ease-in-out;
      }

      .hover\\:shadow-3xl:hover {
        box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25), 0 0 40px rgba(34, 197, 94, 0.3);
      }

      /* 3D Perspective for trial sections */
      .trial-3d-hover {
        perspective: 1000px;
      }

      .trial-3d-hover:hover {
        transform-style: preserve-3d;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 lg:overflow-hidden overflow-auto">
      <div className="flex min-h-screen">
        {/* Features Showcase Section */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex-col justify-start relative lg:overflow-hidden overflow-y-auto lg:max-h-screen border-r border-gray-700/50">
          {/* Modern Background Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600/20 to-purple-600/20"></div>
            {/* Geometric Shapes */}
            <div className="absolute top-10 right-10 w-40 h-40 border border-blue-400/30 rotate-45 animate-spin" style={{animationDuration: '20s'}}></div>
            <div className="absolute bottom-20 left-20 w-32 h-32 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-br from-cyan-400/25 to-blue-400/25 rounded-lg rotate-12 animate-bounce" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-1/3 left-1/3 w-16 h-16 bg-gradient-to-br from-pink-400/30 to-purple-400/30 rounded-full animate-ping" style={{animationDelay: '2s'}}></div>
            {/* Mesh Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1)_0%,transparent_50%)] bg-[length:50px_50px]"></div>
          </div>

          <div className="relative z-10 space-y-6">
            {/* Logo Header */}
            <div className="text-center pt-4">
              <div className="flex justify-center mb-6">
                <img
                  src="/assets/logo.png"
                  alt="Innovative HRMS Logo"
                  className="h-24 w-auto lg:h-32 object-contain drop-shadow-2xl"
                  onError={(e) => {
                    // Fallback to text logo if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <div className="hidden text-4xl lg:text-5xl font-bold text-white drop-shadow-2xl">
                  Innovative HRMS
                </div>
              </div>
              <p className="text-lg text-blue-100 leading-relaxed px-4 drop-shadow-lg font-medium">
                Complete Workforce Management Solution
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-2 px-2">
              {[
                { icon: CameraIcon, title: "Selfie Attendance", desc: "Eliminate biometric devices & save costs", color: "from-blue-500 to-cyan-500", bgColor: "bg-blue-50", iconBg: "bg-gradient-to-r from-blue-500 to-cyan-500" },
                { icon: MapPinIcon, title: "Location Restriction", desc: "Branch-wise accurate attendance", color: "from-green-500 to-emerald-500", bgColor: "bg-green-50", iconBg: "bg-gradient-to-r from-green-500 to-emerald-500" },
                { icon: UserGroupIcon, title: "Employee Management", desc: "All records in one place", color: "from-purple-500 to-violet-500", bgColor: "bg-purple-50", iconBg: "bg-gradient-to-r from-purple-500 to-violet-500" },
                { icon: CalendarIcon, title: "Leave Management", desc: "Apply, approve & track easily", color: "from-orange-500 to-red-500", bgColor: "bg-orange-50", iconBg: "bg-gradient-to-r from-orange-500 to-red-500" },
                { icon: CurrencyDollarIcon, title: "Custom Salary", desc: "Flexible pay configurations", color: "from-yellow-500 to-amber-500", bgColor: "bg-yellow-50", iconBg: "bg-gradient-to-r from-yellow-500 to-amber-500" },
                { icon: CalculatorIcon, title: "Taxation Settings", desc: "PF, ESIC, TDS with custom slabs", color: "from-indigo-500 to-blue-500", bgColor: "bg-indigo-50", iconBg: "bg-gradient-to-r from-indigo-500 to-blue-500" },
                { icon: DocumentTextIcon, title: "Payroll Generation", desc: "Quick & accurate processing", color: "from-pink-500 to-rose-500", bgColor: "bg-pink-50", iconBg: "bg-gradient-to-r from-pink-500 to-rose-500" },
                { icon: ChartBarIcon, title: "Statutory Reports", desc: "PF, ESIC & TDS ready", color: "from-teal-500 to-cyan-500", bgColor: "bg-teal-50", iconBg: "bg-gradient-to-r from-teal-500 to-cyan-500" },
                // Additional Features
                { icon: CreditCardIcon, title: "💸 Loan & Advance Management", desc: "Apply, approve & track loans easily", color: "from-green-500 to-emerald-500", bgColor: "bg-green-50", iconBg: "bg-gradient-to-r from-green-500 to-emerald-500" },
                { icon: CubeIcon, title: "💻 Asset Management", desc: "Track company assets efficiently", color: "from-blue-500 to-indigo-500", bgColor: "bg-blue-50", iconBg: "bg-gradient-to-r from-blue-500 to-indigo-500" },
                { icon: DocumentDuplicateIcon, title: "📝 Letter & Document Templates", desc: "Generate professional letters instantly", color: "from-purple-500 to-pink-500", bgColor: "bg-purple-50", iconBg: "bg-gradient-to-r from-purple-500 to-pink-500" },
                { icon: BriefcaseIcon, title: "🧩 Job Placement & Interview Mgmt", desc: "Coming Soon", color: "from-gray-400 to-gray-500", bgColor: "bg-gray-50", iconBg: "bg-gradient-to-r from-gray-400 to-gray-500", comingSoon: true }
              ].map((feature, index) => (
                <div key={index} className={`${feature.bgColor} backdrop-blur-sm rounded-xl p-3 border border-white/30 hover:scale-105 transition-all duration-300 group shadow-lg hover:shadow-xl ${feature.comingSoon ? 'opacity-75' : ''}`}>
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 p-1.5 rounded-lg ${feature.iconBg} shadow-md group-hover:shadow-lg transition-all duration-300 ${feature.comingSoon ? 'grayscale' : ''}`}>
                      <feature.icon className="h-4 w-4 text-white group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-bold text-gray-800 group-hover:text-gray-900 transition-colors duration-300 mb-1 ${feature.comingSoon ? 'text-gray-600' : ''}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300 leading-tight ${feature.comingSoon ? 'text-gray-500 italic' : ''}`}>
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Modern CTA */}
            <div className="bg-gradient-to-br from-white/10 via-blue-500/10 to-purple-500/10 backdrop-blur-xl rounded-xl p-3 border border-white/20 shadow-2xl mx-2 mb-2 hover:shadow-3xl transition-all duration-700 transform hover:scale-[1.01] hover:-rotate-1 hover:translate-y-1 relative overflow-hidden group">
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              {/* Floating Particles */}
              <div className="absolute top-1 right-2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-ping opacity-60" style={{animationDelay: '0s'}}></div>
              <div className="absolute top-3 right-4 w-1 h-1 bg-purple-400 rounded-full animate-ping opacity-40" style={{animationDelay: '1s'}}></div>
              <div className="absolute bottom-2 left-3 w-1 h-1 bg-pink-400 rounded-full animate-ping opacity-50" style={{animationDelay: '2s'}}></div>

              <div className="relative z-10 text-center transform transition-transform duration-500 group-hover:scale-105">
                {/* Premium Badge */}
                <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 text-white px-3 py-1 rounded-full text-xs font-bold mb-2 shadow-lg transform transition-all duration-300 hover:scale-110 hover:rotate-3">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse mr-1"></div>
                  ✨ PREMIUM FREE TRIAL
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse ml-1"></div>
                </div>

                <h3 className="text-lg font-bold text-white mb-2 transform transition-all duration-300 group-hover:scale-105 group-hover:-translate-y-1">
                  Start Your FREE Trial Today!
                </h3>

                <div className="flex justify-center space-x-2 mb-3">
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1 border border-white/20 hover:bg-white/25 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-green-300" />
                      <span className="text-white text-xs font-medium">No Card</span>
                    </div>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1 border border-white/20 hover:bg-white/25 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-blue-300" />
                      <span className="text-white text-xs font-medium">14-Days Free</span>
                    </div>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1 border border-white/20 hover:bg-white/25 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-purple-300" />
                      <span className="text-white text-xs font-medium">Full Access (As Per Plan)</span>
                    </div>
                  </div>
                </div>

                <Link
                  to="/onboarding"
                  className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-white to-gray-100 text-gray-800 font-bold text-sm rounded-lg hover:from-gray-100 hover:to-white transition-all duration-500 shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-1 hover:rotate-1 relative overflow-hidden group/btn"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10 flex items-center transform transition-transform duration-300 group-hover/btn:scale-105">
                    🚀 Get Started FREE
                    <ArrowRightIcon className="ml-2 h-4 w-4 transform transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:scale-110" />
                  </span>
                </Link>

                {/* <p className="mt-2 text-xs text-blue-100 transform transition-all duration-300 group-hover:scale-105 group-hover:translate-y-1">
                  🔥 Join 1000+ companies already using our HRMS
                </p> */}
              </div>
            </div>
          </div>
        </div>

        {/* Login Form Section */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden">
          {/* Modern Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50">
            {/* Animated Background Elements */}
            <div className="absolute top-10 left-10 w-64 h-64 bg-gradient-to-r from-blue-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-80 h-80 bg-gradient-to-r from-pink-400/8 to-orange-400/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-300/5 to-blue-300/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>

            {/* Modern Geometric Elements */}
            <div className="absolute top-20 right-20 w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full animate-bounce shadow-lg" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute bottom-32 left-16 w-4 h-4 bg-gradient-to-br from-pink-500 to-orange-500 rounded-full animate-ping shadow-lg" style={{animationDelay: '1.5s'}}></div>
            <div className="absolute top-1/4 left-1/4 w-8 h-8 border-2 border-blue-300/50 rotate-45 animate-spin" style={{animationDuration: '15s'}}></div>
            <div className="absolute bottom-1/4 right-1/4 w-12 h-12 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-lg rotate-12 animate-pulse shadow-xl" style={{animationDelay: '3s'}}></div>

            {/* Subtle Grid Pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          </div>

          <div className="w-full max-w-md space-y-8 relative z-10">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <img
                src="/assets/logo.png"
                alt="Innovative HRMS Logo"
                className="h-12 w-auto mx-auto mb-4 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <div className="hidden text-2xl font-bold text-primary-600">
                Innovative HRMS
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Welcome Back
              </h2>
              <p className="text-gray-600 mt-2">
                Sign in to your account
              </p>
            </div>

            {/* Mobile Features Preview */}
            <div className="lg:hidden mb-6">
              <h3 className="text-lg font-bold text-gray-900 text-center mb-4">
                🚀 Powerful HRMS Features
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: CameraIcon, title: "Selfie Attendance", color: "bg-blue-500" },
                  { icon: MapPinIcon, title: "Location Control", color: "bg-green-500" },
                  { icon: UserGroupIcon, title: "Employee Mgmt", color: "bg-purple-500" },
                  { icon: CalendarIcon, title: "Leave Tracking", color: "bg-orange-500" },
                  { icon: CreditCardIcon, title: "Loan Mgmt", color: "bg-green-500" },
                  { icon: CubeIcon, title: "Asset Mgmt", color: "bg-blue-500" },
                  { icon: DocumentDuplicateIcon, title: "Letter Templates", color: "bg-purple-500" },
                  { icon: CurrencyDollarIcon, title: "Payroll", color: "bg-yellow-500" }
                ].map((feature, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex items-center space-x-2">
                      <div className={`p-1.5 rounded-md ${feature.color} text-white`}>
                        <feature.icon className="h-4 w-4" />
                      </div>
                      <span className="text-xs font-medium text-gray-700 truncate">{feature.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modern Mobile Trial Section */}
            <div className="lg:hidden bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-2xl p-5 mb-6 shadow-2xl border border-blue-500/30 hover:shadow-3xl transition-all duration-700 transform hover:scale-[1.02] hover:-rotate-1 hover:translate-y-1 relative overflow-hidden group">
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-pink-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              {/* Floating Particles */}
              <div className="absolute top-2 right-4 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-80" style={{animationDelay: '0s'}}></div>
              <div className="absolute top-6 right-8 w-1 h-1 bg-white rounded-full animate-ping opacity-60" style={{animationDelay: '1s'}}></div>
              <div className="absolute bottom-4 left-6 w-1.5 h-1.5 bg-cyan-300 rounded-full animate-ping opacity-70" style={{animationDelay: '2s'}}></div>

              <div className="relative z-10 text-center transform transition-transform duration-500 group-hover:scale-105">
                {/* Premium Badge */}
                <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 text-white px-3 py-1 rounded-full text-xs font-bold mb-3 shadow-lg transform transition-all duration-300 hover:scale-110 hover:rotate-3">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse mr-1"></div>
                  ✨ PREMIUM FREE TRIAL
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse ml-1"></div>
                </div>

                <h3 className="text-lg font-bold text-white mb-3 transform transition-all duration-300 group-hover:scale-105 group-hover:-translate-y-1">
                  Start Your FREE Trial Today!
                </h3>

                <div className="flex justify-center space-x-2 mb-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/30 hover:bg-white/30 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-green-300" />
                      <span className="text-white text-xs font-medium">No Card</span>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/30 hover:bg-white/30 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-blue-300" />
                      <span className="text-white text-xs font-medium">14-Day Free</span>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/30 hover:bg-white/30 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center space-x-1">
                      <CheckCircleIcon className="h-3 w-3 text-purple-300" />
                      <span className="text-white text-xs font-medium">Full Access (As Per Plan)</span>
                    </div>
                  </div>
                </div>

                <Link
                  to="/onboarding"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-white to-blue-50 text-blue-800 font-bold rounded-lg hover:from-blue-50 hover:to-white transition-all duration-500 shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-2 hover:rotate-1 relative overflow-hidden group/btn border border-white/50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10 flex items-center text-sm transform transition-transform duration-300 group-hover/btn:scale-105">
                    🚀 Get Started FREE
                    <ArrowRightIcon className="ml-2 h-4 w-4 transform transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:scale-110" />
                  </span>
                </Link>
              </div>
            </div>

            {/* Login Form */}
            <div className="bg-white/90 backdrop-blur-xl shadow-2xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-white/20 hover:shadow-3xl transition-all duration-500 animate-fade-in-up relative overflow-hidden">
              {/* Card Background Effects */}
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/50 to-transparent pointer-events-none"></div>
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-lg animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-16 h-16 bg-gradient-to-br from-pink-400/20 to-orange-400/20 rounded-full blur-lg animate-pulse" style={{animationDelay: '1s'}}></div>
              {/* Desktop Header */}
              <div className="hidden lg:block text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900">
                  {t('auth:loginTitle')}
                </h2>
                <p className="text-gray-600 mt-2">
                  {t('auth:loginSubtitle')}
                </p>
              </div>

              <form className="space-y-6" onSubmit={formik.handleSubmit}>
                {/* Success Message */}
                {successMessage && (
                  <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                    <div className="flex">
                      <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                      <p className="ml-3 text-sm text-green-800">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {authError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <p className="ml-3 text-sm text-red-800">{authError}</p>
                    </div>
                  </div>
                )}

                <div className="animate-fade-in-up group" style={{animationDelay: '0.1s', animation: 'fadeInUp 0.6s ease-out forwards'}}>
                  <label htmlFor="email" className="form-label group-hover:text-blue-600 transition-colors duration-300">
                    {t('auth:emailLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 group-hover:scale-110">
                      <UserIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors duration-300" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={`form-input pl-10 transition-all duration-300 hover:shadow-md focus:shadow-lg focus:ring-2 focus:ring-blue-500/20 ${
                        formik.touched.email && formik.errors.email
                          ? 'border-red-300 focus:ring-red-500/20'
                          : 'hover:border-blue-300'
                      }`}
                      placeholder={t('auth:emailPlaceholder')}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <p className="form-error animate-shake">{formik.errors.email}</p>
                  )}
                </div>

                {/* Password Field */}
                <div className="animate-fade-in-up group" style={{animationDelay: '0.2s'}}>
                  <label htmlFor="password" className="form-label group-hover:text-blue-600 transition-colors duration-300">
                    {t('auth:passwordLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-300 group-hover:scale-110">
                      <LockClosedIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors duration-300" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`form-input pl-10 pr-10 transition-all duration-300 hover:shadow-md focus:shadow-lg focus:ring-2 focus:ring-blue-500/20 ${
                        formik.touched.password && formik.errors.password
                          ? 'border-red-300 focus:ring-red-500/20'
                          : 'hover:border-blue-300'
                      }`}
                      placeholder={t('auth:passwordPlaceholder')}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500 transition-all duration-300 hover:scale-110 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="form-error animate-shake">{formik.errors.password}</p>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={formik.values.rememberMe}
                      onChange={formik.handleChange}
                    />
                    <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                      {t('auth:rememberMe')}
                    </label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('auth:forgotPassword')}
                  </Link>
                </div>

                {/* Submit Button */}
                <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                  <button
                    type="submit"
                    disabled={authLoading || !formik.isValid}
                    className="w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-lg text-sm font-bold text-white bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500 transform hover:scale-105 hover:shadow-2xl hover:-translate-y-1 relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <span className="relative z-10 flex items-center">
                      {authLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('auth:loggingIn')}
                        </>
                      ) : (
                        <>
                          {t('auth:loginButton')}
                          <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                </div>

                {/* New User Link */}
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    New to Innovative HRMS?{' '}
                    <Link
                      to="/onboarding"
                      className="font-medium text-primary-600 hover:text-primary-500"
                    >
                      Get started with a free trial
                    </Link>
                  </p>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="text-center text-sm text-gray-500 space-y-1">
              <div>
                Developed by:{' '}
                <a
                  href="https://iarinfotech.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
                >
                  Innovative AR Infotech
                </a>
              </div>
              <div>
                Support:{' '}
                <a
                  href="mailto:inovativearinfotech@gmail.com"
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
                >
                  inovativearinfotech@gmail.com
                </a>
              </div>
              <div className="pt-2">
                © {new Date().getFullYear()} Innovative HRMS. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
